/**
 * Main Application Component
 * Sets up routing, theme, authentication, socket providers, and main layout
 * Implements React Router v6 with lazy loading and code splitting
 */

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography, Button } from '@mui/material';
import theme from './theme';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';

/**
 * ErrorBoundary — catches any render-time JS error so the app
 * shows a friendly message instead of a blank white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#B71C1C' }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3, maxWidth: 480 }}>
            We hit an unexpected error. Please refresh the page — if the problem persists,
            try clearing your browser cache.
          </Typography>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <Typography
              variant="caption"
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: '#fff',
                borderRadius: 1,
                fontFamily: 'monospace',
                color: '#333',
                maxWidth: 600,
                wordBreak: 'break-word',
              }}
            >
              {String(this.state.error)}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: '#B71C1C', '&:hover': { backgroundColor: '#7F0000' } }}
          >
            Refresh Page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Lazy load pages for code splitting
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Leadership = React.lazy(() => import('./pages/Leadership'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const ForgotPassword = React.lazy(() => import('./pages/auth/ForgotPassword'));
const SubmitProject = React.lazy(() => import('./pages/SubmitProject'));
const RFPListings = React.lazy(() => import('./pages/RFPListings'));
const RFPDetail = React.lazy(() => import('./pages/RFPDetail'));
const HomeownerDashboard = React.lazy(() => import('./pages/HomeownerDashboard'));
const ContractorDashboard = React.lazy(() => import('./pages/ContractorDashboard'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const ContractorProfile = React.lazy(() => import('./pages/ContractorProfile'));
const Messaging = React.lazy(() => import('./pages/Messaging'));
const Blog = React.lazy(() => import('./pages/Blog'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const ReviewWorkOrder = React.lazy(() => import('./pages/ReviewWorkOrder'));
const ContractorEditProfile = React.lazy(() => import('./pages/ContractorEditProfile'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const ConfirmEmailChange = React.lazy(() => import('./pages/auth/ConfirmEmailChange'));
const BrowseProjects = React.lazy(() => import('./pages/BrowseProjects'));
const MembershipPage     = React.lazy(() => import('./pages/MembershipPage'));
const HomeownerUpgrade   = React.lazy(() => import('./pages/HomeownerUpgrade'));
const ContractDetail = React.lazy(() => import('./pages/ContractDetail'));
const DisputeCenter  = React.lazy(() => import('./pages/DisputeCenter'));
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage'));
const LaunchPage = React.lazy(() => import('./pages/LaunchPage'));
const HowItWorks = React.lazy(() => import('./pages/HowItWorks'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ShareRedirect = React.lazy(() => import('./pages/ShareRedirect'));
const LeaveReview   = React.lazy(() => import('./pages/LeaveReview'));

/**
 * Main App component
 * Wraps entire application with providers and routes
 */
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SocketProvider>
            <Router>
              <ScrollToTop />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100vh',
                }}
              >
                {/* Navigation header */}
                <Navigation />

                {/* Main content area */}
                <Box sx={{ flex: 1 }}>
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<Home />} />
                        <Route path="/launch" element={<LaunchPage />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/leadership" element={<Leadership />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />

                        {/* Public RFP browsing */}
                        <Route path="/rfps" element={<RFPListings />} />
                        <Route path="/rfps/:id" element={<RFPDetail />} />
                        <Route path="/projects/browse" element={<BrowseProjects />} />
                        <Route path="/contractors/:id" element={<ContractorProfile />} />

                        {/* Auth routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                        <Route path="/confirm-email-change/:token" element={<ConfirmEmailChange />} />

                        {/* Protected homeowner routes */}
                        <Route
                          path="/submit-project"
                          element={
                            <ProtectedRoute requiredRole="homeowner">
                              <SubmitProject />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dashboard/homeowner"
                          element={
                            <ProtectedRoute requiredRole="homeowner">
                              <HomeownerDashboard />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/review-work-order/:projectId"
                          element={
                            <ProtectedRoute requiredRole="homeowner">
                              <ReviewWorkOrder />
                            </ProtectedRoute>
                          }
                        />
                        {/* Protected contractor routes */}
                        <Route
                          path="/dashboard/contractor"
                          element={
                            <ProtectedRoute requiredRole="contractor">
                              <ContractorDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile/contractor"
                          element={
                            <ProtectedRoute requiredRole="contractor">
                              <ContractorEditProfile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/membership"
                          element={
                            <ProtectedRoute requiredRole="contractor">
                              <MembershipPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/membership/success"
                          element={
                            <ProtectedRoute requiredRole="contractor">
                              <MembershipPage />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/homeowner-upgrade"
                          element={
                            <ProtectedRoute requiredRole="homeowner">
                              <HomeownerUpgrade />
                            </ProtectedRoute>
                          }
                        />

                        {/* Protected admin routes */}
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AdminPanel />
                            </ProtectedRoute>
                          }
                        />

                        {/* Contract detail — accessible to both parties */}
                        <Route
                          path="/contracts/:id"
                          element={
                            <ProtectedRoute>
                              <ContractDetail />
                            </ProtectedRoute>
                          }
                        />

                        {/* Leave a review for a completed contract */}
                        <Route
                          path="/contracts/:contractId/review"
                          element={
                            <ProtectedRoute>
                              <LeaveReview />
                            </ProtectedRoute>
                          }
                        />

                        {/* Dispute Resolution Center — open new dispute or view existing */}
                        <Route
                          path="/disputes/:id"
                          element={
                            <ProtectedRoute>
                              <DisputeCenter />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dispute-center"
                          element={
                            <ProtectedRoute>
                              <DisputeCenter />
                            </ProtectedRoute>
                          }
                        />

                        {/* Protected messaging (all authenticated users) */}
                        <Route
                          path="/messages"
                          element={
                            <ProtectedRoute>
                              <Messaging />
                            </ProtectedRoute>
                          }
                        />

                        {/* Account settings (all authenticated users) */}
                        <Route
                          path="/account"
                          element={
                            <ProtectedRoute>
                              <AccountPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Social share click-through redirect */}
                        <Route path="/share/:token" element={<ShareRedirect />} />

                        {/* 404 Not Found */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </Box>

                {/* Footer */}
                <Footer />
              </Box>
            </Router>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
